<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('INITIATE_TRANSFER')"
            :label="$t('Initiate Transfer')"
            size="md"
            class="mt-0"
            @click="goToInitiateTransfer"
          />

          <b-dropdown
            v-if="getUserPermission('VIEW_TRANSFER_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
            class="ml-1"
          >
            <b-dropdown-item @click="csvExport">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport">
              {{ $t('Excel') }}
            </b-dropdown-item>
          </b-dropdown>
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">

            <feather-icon
              icon="InfoIcon"
              size="16"
              class="align-middle text-body mr-1"
              @click="onViewInfo"
            />
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="customerInfoRef"
      :items="outwardTransferList"
      responsive
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(financialDate)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(transactionDate)="data">
        {{ toDateWithTime(data.value) }}
      </template>
      <template #cell(status)="data">
        <b-badge
          v-if="data.value"
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div
          class="text-nowrap"
        >
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('INITIATE_TRANSFER') && data.item.status === 'PendingTSQ' || data.item.status === 'Processed'"
              @click="onSelectRequeryStatus(data.item, $event.target)"
            >
              <feather-icon icon="RefreshCcwIcon" />
              <span class="align-middle ml-50"> {{ $t('Requery Status') }} </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="getUserPermission('RETRY_TRANSFER') && data.item.allowRetry === true"
              @click="onSelectRetryTransfer(data.item, $event.target)"
            >
              <feather-icon icon="SendIcon" />
              <span class="align-middle ml-50"> {{ $t('Retry Transfer') }} </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="getUserPermission('VIEW_TRANSFER_REPORT')"
              @click="onPrintReceipt(data.item, $event.target)"
            >
              <feather-icon icon="BookIcon" />
              <span class="align-middle ml-50"> {{ $t('Print Transaction Receipt') }} </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="getUserPermission('VIEW_TRANSFER_REPORT')"
              @click="onDownloadReceipt(data.item.id, $event.target)"
            >
              <feather-icon icon="BookIcon" />
              <span class="align-middle ml-50"> {{ $t('Download Transaction Receipt') }} </span>
            </b-dropdown-item>
          </b-dropdown></div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />
    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchOutwardTransfer"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Account Name -->
        <b-col md="6">
          <InputField
            id="account-name"
            v-model="filter.accountName"
            :label="$t('Account Name')"
            :placeholder="$t('Enter account name')"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Account Name -->

        <!-- Account Number -->
        <b-col md="6">
          <InputField
            id="account-number"
            v-model="filter.accountNumber"
            :label="$t('Account Number')"
            :placeholder="$t('Enter account number')"
            type="text"
          />
        </b-col>
        <!-- Account Number -->

        <!-- Branch -->
        <b-col md="6">
          <SelectField
            id="branch"
            v-model="filter.branchCode"
            :label="$t('Branch')"
            :placeholder="$t('Select Branch')"
            :options="branchOptions"
            :reduce="option => option.value"
            item="label"
          />
        </b-col>
        <!-- Branch -->

        <!-- Beneficiary Bank -->
        <b-col md="6">
          <SelectField
            id="beneficiary-bank"
            v-model="filter.beneficiaryBank"
            :label="$t('Beneficiary Bank')"
            :placeholder="$t('Select Beneficiary Bank')"
            :options="bankListOptions"
            :reduce="option => option.value"
            item="label"
          />
        </b-col>
        <!-- Beneficiary Bank -->

        <!--Beneficiary Account Number -->
        <b-col md="6">
          <InputField
            id="reference-number"
            v-model="filter.beneficiaryAccountNumber"
            :label="$t('Beneficiary Account Number')"
            :placeholder="$t('Enter Beneficiary Account Number')"
            type="number"
          />
        </b-col>
        <!-- Beneficiary Account Number -->

        <!-- Initiator -->
        <b-col md="6">
          <SelectField
            id="beneficiary-bank"
            v-model="filter.initiator"
            :label="$t('Initiator')"
            :placeholder="$t('Select Initiator')"
            :options="usersOptions"
            :reduce="option => option.value"
            item="label"
          />
        </b-col>
        <!-- Initiator -->
        <!-- Transaction Date From -->
        <b-col md="6">
          <DatePickerField
            id="transaction-date-from"
            v-model="filter.transactionDateFrom"
            :label="$t('Transaction Date (From)')"
            :config="datePickerConfig"
          />
        </b-col>
        <!-- Transaction Date From-->

        <!-- Transaction Date To -->
        <b-col md="6">
          <DatePickerField
            id="transaction-date-to"
            v-model="filter.transactionDateTo"
            :label="$t('Transaction Date (To)')"
            :config="datePickerConfig"
          />
        </b-col>
        <!-- Transaction Date To-->
        <b-col md="6">
          <SelectField
            id="gateway-provider"
            v-model="filter.gateway"
            :label="$t('Gateway')"
            :options="gatewayProviderOptions"
            item="label"
            :reduce="option => option"
            :placeholder="$t('Select')"
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="postings-reference"
            v-model="filter.postingsReference"
            :label="$t('Postings Reference')"
            :placeholder="$t('Enter Postings Reference')"
            type="text"
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="transaction-reference"
            v-model="filter.transactionId"
            :label="$t('Transaction Reference')"
            :placeholder="$t('Enter Transaction Reference')"
            type="text"
          />
        </b-col>

        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :placeholder="$t('Select')"
            :options="translatedStatusOptions"
            item="label"
            :reduce="option => option.value"
          />
        </b-col>
        <!-- Status -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <!-- Print Section -->
    <div id="transaction-printer">
      <p id="prints">
        <b-card
          no-body
          class="overflow-hidden"
        >
          <div class="transaction-container">
            <div class="card receipt-card">
              <div class="card-body">
                <div class="list-group transaction-details">
                  <div class="detail-item">
                    <div class="detail-row">
                      <span class="detail-label receipt-logo">
                        <img
                          v-if="userDetails.institutionLogoURL"
                          :src="userDetails.institutionLogoURL"
                          :alt="$t('Institution Logo')"
                        >
                      </span>
                      <h4 class="receipt-title">
                        {{ $t('Transaction Receipt') }}
                      </h4>
                    </div>
                  </div>
                </div>

                <!-- Amount Section -->
                <div class="amount-section mt-1">
                  <h2 class="amount-label">
                    {{ $t('Transaction Amount') }}
                  </h2>
                  <h3 class="amount-value">
                    {{ printableData.amount }}
                  </h3>
                  <b-badge
                    v-if="printableData.status"
                    pill
                    :variant="getStatusColor(printableData.status)"
                  >
                    {{ $t(printableData.status) }}
                  </b-badge>
                </div>

                <!-- Transaction Details -->
                <div class="list-group transaction-details">
                  <div class="inner">
                    <img
                      v-if="userDetails.institutionLogoURL"
                      :src="userDetails.institutionLogoURL"
                      :alt="$t('Institution Logo')"
                    >
                  </div>

                  <div class="list-group-item detail-item">
                    <div class="detail-row">
                      <span class="detail-label">{{ $t('Sender Details') }}</span>
                      <span class="detail-value">
                        {{ printableData.accountName }}
                        <br>
                        <small>{{ corporateDetails.registeredName }} | {{ printableData.accountNumber }}</small>
                      </span>
                    </div>
                  </div>

                  <div class="list-group-item detail-item">
                    <div class="detail-row">
                      <span class="detail-label">{{ $t('Recipient Details') }}</span>
                      <span class="detail-value">
                        {{ printableData.beneficiaryAccountName }}
                        <br>
                        <small>{{ printableData.beneficiaryBankName }} | {{ printableData.beneficiaryAccountNumber }}</small>
                      </span>
                    </div>
                  </div>

                  <div class="list-group-item detail-item">
                    <div class="detail-row">
                      <span class="detail-label">{{ $t('Transaction Date') }}</span>
                      <span class="detail-value">
                        {{ printableData.transactionDate ? toDateTimeFormat(printableData.transactionDate) : '' }}
                      </span>
                    </div>
                  </div>

                  <div class="list-group-item detail-item">
                    <div class="detail-row">
                      <span class="detail-label">{{ $t('Fees') }}</span>
                      <span class="detail-value">{{ printableData.fee }}</span>
                    </div>
                  </div>

                  <div class="list-group-item detail-item">
                    <div class="detail-row">
                      <span class="detail-label">{{ $t('Transaction Type') }}</span>
                      <span class="detail-value">Outward</span>
                    </div>
                  </div>

                  <div class="list-group-item detail-item">
                    <div class="detail-row">
                      <span class="detail-label">{{ $t('Transaction Reference') }}</span>
                      <span class="detail-value">{{ printableData.transactionReference }}</span>
                    </div>
                  </div>

                  <div class="list-group-item detail-item">
                    <div class="detail-row">
                      <span class="detail-label">{{ $t('Narration') }}</span>
                      <span class="detail-value">{{ printableData.beneficiaryNarration }}</span>
                    </div>
                  </div>
                </div>

                <!-- This repeats on every printed page -->
                <div
                  v-if="institutionDetails.regulatoryFooter"
                  class="print-footer"
                >
                  {{ institutionDetails.regulatoryFooter }}
                </div>

                <!-- Disclaimer -->
                <div class="disclaimer">
                  <strong>{{ $t('Disclaimer') }}:</strong><br>
                  {{ $t('You have successfully sent money to the recipient. But wait, this message does not mean they have the money in their account just yet. Due to the nature of the transfer, you understand that we cannot be held responsible for notifications, delays, errors, and or blacklists. The bank cannot be held responsible for any of these issues beyond its control. Please bear with us while we help sort out your transaction status.') }}
                  <br>{{ $t('Thanks for trusting with us!') }}
                </div>
              </div>
            </div>
          </div>
        </b-card>
      </p>
    </div>
    <!-- Print Section -->

    <b-modal
      id="info-modal"
      ref="infoModal"
      centered
      size="md"
      :title="$t('CBA Response Codes')"
      ok-only
      :ok-title="$t('Okay')"
      @ok="hideModal('info-modal')"
    >
      <div class="p-1">
        <table>
          <tr
            style="font-size: 0.9rem; text-align: justify; line-height: 1.5; font-family: Century Gothic, sans-serif;"
            class="mb-3"
          >
            <td style="width: 10%;">
              {{ $t('Successful') }}
            </td>
            <td style="width: 5%">
              =
            </td>
            <td style="width: 10%">
              00
            </td>
          </tr>
          <tr
            style="font-size: 0.9rem; text-align: justify; line-height: 1.5; font-family: Century Gothic, sans-serif;"
            class="mb-3"
          >
            <td style="width: 10%;">
              {{ $t('SQL Exception') }}
            </td>
            <td style="width: 5%">
              =
            </td>
            <td style="width: 10%">
              06
            </td>
          </tr>
          <tr
            style="font-size: 0.9rem; text-align: justify; line-height: 1.5; font-family: Century Gothic, sans-serif;"
            class="mb-3"
          >
            <td style="width: 10%;">
              {{ $t('SQL Timeout Exception') }}
            </td>
            <td style="width: 5%">
              =
            </td>
            <td style="width: 10%">
              06
            </td>
          </tr>
          <tr
            style="font-size: 0.9rem; text-align: justify; line-height: 1.5; font-family: Century Gothic, sans-serif;"
            class="mb-3"
          >
            <td style="width: 10%;">
              {{ $t('Endpoint Timeout Exception') }}
            </td>
            <td style="width: 5%">
              =
            </td>
            <td style="width: 10%">
              06
            </td>
          </tr>
          <tr
            style="font-size: 0.9rem; text-align: justify; line-height: 1.5; font-family: Century Gothic, sans-serif;"
            class="mb-3"
          >
            <td style="width: 10%;">
              {{ $t('General Error') }}
            </td>
            <td style="width: 5%">
              =
            </td>
            <td style="width: 10%">
              06
            </td>
          </tr>
          <tr
            style="font-size: 0.9rem; text-align: justify; line-height: 1.5; font-family: Century Gothic, sans-serif;"
            class="mb-3"
          >
            <td style="width: 10%;">
              {{ $t('Invalid Elastic Table Name') }}
            </td>
            <td style="width: 5%">
              =
            </td>
            <td style="width: 10%">
              06
            </td>
          </tr>
          <tr
            style="font-size: 0.9rem; text-align: justify; line-height: 1.5; font-family: Century Gothic, sans-serif;"
            class="mb-3"
          >
            <td style="width: 10%;">
              {{ $t('Posting Object Empty') }}
            </td>
            <td style="width: 5%">
              =
            </td>
            <td style="width: 10%">
              06
            </td>
          </tr>
          <tr
            style="font-size: 0.9rem; text-align: justify; line-height: 1.5; font-family: Century Gothic, sans-serif;"
            class="mb-3"
          >
            <td style="width: 10%;">
              {{ $t('Model Validation Error') }}
            </td>
            <td style="width: 5%">
              =
            </td>
            <td style="width: 10%">
              09
            </td>
          </tr>
          <tr
            style="font-size: 0.9rem; text-align: justify; line-height: 1.5; font-family: Century Gothic, sans-serif;"
            class="mb-3"
          >
            <td style="width: 10%;">
              {{ $t('Invalid Amount') }}
            </td>
            <td style="width: 5%">
              =
            </td>
            <td style="width: 10%">
              13
            </td>
          </tr>
          <tr
            style="font-size: 0.9rem; text-align: justify; line-height: 1.5; font-family: Century Gothic, sans-serif;"
            class="mb-3"
          >
            <td style="width: 10%;">
              {{ $t('No Record Found') }}
            </td>
            <td style="width: 5%">
              =
            </td>
            <td style="width: 10%">
              25
            </td>
          </tr>
          <tr
            style="font-size: 0.9rem; text-align: justify; line-height: 1.5; font-family: Century Gothic, sans-serif;"
            class="mb-3"
          >
            <td style="width: 10%;">
              {{ $t('Duplicate Record') }}
            </td>
            <td style="width: 5%">
              =
            </td>
            <td style="width: 10%">
              26
            </td>
          </tr>
          <tr
            style="font-size: 0.9rem; text-align: justify; line-height: 1.5; font-family: Century Gothic, sans-serif;"
            class="mb-3"
          >
            <td style="width: 10%;">
              {{ $t('Force Debit Not Allowed') }}
            </td>
            <td style="width: 5%">
              =
            </td>
            <td style="width: 10%">
              28
            </td>
          </tr>
          <tr
            style="font-size: 0.9rem; text-align: justify; line-height: 1.5; font-family: Century Gothic, sans-serif;"
            class="mb-3"
          >
            <td style="width: 15%;">
              {{ $t('Database Update Not Executed') }}
            </td>
            <td style="width: 5%">
              =
            </td>
            <td style="width: 10%">
              28
            </td>
          </tr>
          <tr
            style="font-size: 0.9rem; text-align: justify; line-height: 1.5; font-family: Century Gothic, sans-serif;"
            class="mb-3"
          >
            <td style="width: 10%;">
              {{ $t('Format Error') }}
            </td>
            <td style="width: 5%">
              =
            </td>
            <td style="width: 10%">
              30
            </td>
          </tr>
          <tr
            style="font-size: 0.9rem; text-align: justify; line-height: 1.5; font-family: Century Gothic, sans-serif;"
            class="mb-3"
          >
            <td style="width: 10%;">
              {{ $t('Insufficient Balance') }}
            </td>
            <td style="width: 5%">
              =
            </td>
            <td style="width: 10%">
              51
            </td>
          </tr>
          <tr
            style="font-size: 0.9rem; text-align: justify; line-height: 1.5; font-family: Century Gothic, sans-serif;"
            class="mb-3"
          >
            <td style="width: 10%;">
              {{ $t('No Check Account') }}
            </td>
            <td style="width: 5%">
              =
            </td>
            <td style="width: 10%">
              52
            </td>
          </tr>

          <tr
            style="font-size: 0.9rem; text-align: justify; line-height: 1.5; font-family: Century Gothic, sans-serif;"
            class="mb-3"
          >
            <td style="width: 10%;">
              {{ $t('Transaction Permission') }}
            </td>
            <td style="width: 5%">
              =
            </td>
            <td style="width: 10%">
              57
            </td>
          </tr>
          <tr
            style="font-size: 0.9rem; text-align: justify; line-height: 1.5; font-family: Century Gothic, sans-serif;"
            class="mb-3"
          >
            <td style="width: 10%;">
              {{ $t('Security Violation') }}
            </td>
            <td style="width: 5%">
              =
            </td>
            <td style="width: 10%">
              63
            </td>
          </tr>
          <tr
            style="font-size: 0.9rem; text-align: justify; line-height: 1.5; font-family: Century Gothic, sans-serif;"
            class="mb-3"
          >
            <td style="width: 10%;">
              {{ $t('CutOff In Progress') }}
            </td>
            <td style="width: 5%">
              =
            </td>
            <td style="width: 10%">
              90
            </td>
          </tr>
          <tr
            style="font-size: 0.9rem; text-align: justify; line-height: 1.5; font-family: Century Gothic, sans-serif;"
            class="mb-3"
          >
            <td style="width: 10%;">
              {{ $t('Exceeds Cash Limit') }}
            </td>
            <td style="width: 5%">
              =
            </td>
            <td style="width: 10%">
              93
            </td>
          </tr>
          <tr
            style="font-size: 0.9rem; text-align: justify; line-height: 1.5; font-family: Century Gothic, sans-serif;"
            class="mb-3"
          >
            <td style="width: 10%;">
              {{ $t('Fatal Error') }}
            </td>
            <td style="width: 5%">
              =
            </td>
            <td style="width: 10%">
              96
            </td>
          </tr>
        </table>
      </div>
    </b-modal>

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable,
  BSpinner, BDropdown, BDropdownItem, BBadge,
  // BCardBody, BCardText,
  // BButton, BListGroup, BListGroupItem,

} from 'bootstrap-vue'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import Ripple from 'vue-ripple-directive'
import { mapActions, mapGetters } from 'vuex'
import * as FileSaver from 'file-saver'
import TablePagination from '@/views/components/table/TablePagination.vue'
import getStatusColor from '../../../../../helpers/getStatusColor'
import { toAmount, toBalance, toFormatAmount } from '@/helpers/formatAmount'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import { toDateWithYear, toDateWithTime, toDateTimeFormat } from '../../../../../helpers/formatDate'
import InputField from '../../../../forms/InputField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import DatePickerField from '../../../../forms/DatePickerField.vue'
import Button from '../../../../components/button/Button.vue'
import { eChannelsPermission } from '../../../../../helpers/permissions'

const statusOptions = [
  { value: 'Pending', label: 'Pending' },
  { value: 'Processing', label: 'Processing' },
  { value: 'Processed', label: 'Processed' },
  { value: 'Reversed', label: 'Reversed' },
  { value: 'PendingTSQ', label: 'Pending TSQ' },
  { value: 'Failed', label: 'Failed' },
  { value: 'ConfirmManually', label: 'Confirm Manually' },
]

const getDefaultFilter = () => ({
  accountName: null,
  accountNumber: null,
  branchCode: null,
  beneficiaryBank: null,
  beneficiaryAccountNumber: null,
  transactionDate: null,
  transactionDateFrom: null,
  transactionDateTo: null,
  initiator: null,
  postingsReference: null,
  transactionId: null,
  gateway: null,
  status: null,
})

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    BBadge,
    BDropdown,
    BDropdownItem,
    TablePagination,
    TableEntries,
    InputField,
    SelectField,
    FullScreenSpinner,
    DatePickerField,
    // BCardBody,
    // BCardText,
    // BButton,
    // BListGroup,
    // BListGroupItem,
  },
  directives: {
    Ripple,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      toDateWithTime,
      toDateTimeFormat,
      getStatusColor,
      toAmount,
      toFormatAmount,
      toBalance,
      isLoadingInitiatorName: false,
      usersOptions: [],
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'beneficiaryAccountNumber', sortable: true, label: 'Beneficiary Account Number' },
        { key: 'beneficiaryAccountName', sortable: true, label: 'Beneficiary Account Name' },
        { key: 'beneficiaryBankName', sortable: true, label: 'Beneficiary Bank' },
        { key: 'amount', sortable: true, label: 'Amount' },
        { key: 'fee', sortable: true, label: 'Fee' },
        { key: 'gateway', sortable: true, label: 'Transfer Gateway' },
        { key: 'transactionReference', sortable: true, label: 'Transaction Reference' },
        { key: 'postingReference', sortable: true, label: 'Postings Reference' },
        { key: 'cbaResponseCode', sortable: true, label: 'CBA Response Code' },
        { key: 'gatewayResponseCode', sortable: true, label: 'Gateway Response Code' },
        { key: 'tsqResponseCode', sortable: true, label: 'TSQ Response Code' },
        { key: 'responseMessage', sortable: true, label: 'Response Message' },
        { key: 'nipMode', sortable: true, label: 'NIP Mode' },
        { key: 'apiRequestIP', sortable: true, label: 'API Request IP' },
        { key: 'apiRequestReference', sortable: true, label: 'API Request Reference' },
        { key: 'initiator', sortable: true, label: 'Initiator' },
        { key: 'approver', sortable: true, label: 'Approver' },
        { key: 'transactionDate', sortable: true, label: 'Transaction Date' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions' },
      ],
      // statusOptions: [
      //   { value: 'Pending', label: 'Pending' },
      //   { value: 'Processing', label: 'Processing' },
      //   { value: 'Processed', label: 'Processed' },
      //   { value: 'Reversed', label: 'Reversed' },
      //   { value: 'PendingTSQ', label: 'Pending TSQ' },
      //   { value: 'Failed', label: 'Failed' },
      //   { value: 'ConfirmManually', label: 'Confirm Manually' },
      // ],
      outwardTransferList: [],
      filter: getDefaultFilter(),
      datePickerConfig: {
        dateFormat: 'Y-m-d',
      },

      showSpinner: false,
      printableData: {},
      gatewayProviderOptions: [],
      institutionDetails: {
        regulatoryFooter: '',
      },
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
      userDetails: 'auth/getCurrentUser',
      corporateDetails: 'operations/getCorporateDetails',
      bankListOptions: 'eChannels/allBankOptions',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedStatusOptions() {
      return statusOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchOutwardTransfer()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchOutwardTransfer()
    },
  },
  mounted() {
    this.fetchOutwardTransfer()
    this.fetchGatewayProviders()
    this.fetchUsers()
    // this.fetchBanks()
    this.fetchInstitutionData()
  },
  methods: {
    ...mapActions({
      getOutwardTransfer: 'eChannels/getOutwardTransfer',
      downloadOutwardTransfers: 'eChannels/downloadOutwardTransfers',
      generateTransactionReceipt: 'eChannels/generateTransactionReceipt',
      performRequeryStatus: 'eChannels/performRequeryStatus',
      performRetryTransfer: 'eChannels/performRetryTransfer',
      getUsers: 'operations/getAllUsers',
      getGatewayProviders: 'eChannels/getGatewayProviders',
      getInstitutionData: 'operations/getInstitutionFileUploadList',
    }),
    getSearchFilterPayload() {
      return {
        ...this.filter,
      }
    },
    onViewInfo() {
      this.$root.$emit('bv::show::modal', 'info-modal')
    },
    goToInitiateTransfer() {
      this.$router.push('initiate-outward-transfer')
    },
    async fetchInstitutionData() {
      try {
        const response = await this.getInstitutionData({
          permission: eChannelsPermission.VIEW_TRANSFER_REPORT,
        })
        const { status, data } = response
        if (status === 200 && data) {
          this.institutionDetails = {
            regulatoryFooter: data.regulatoryFooter || '',
          }
        }
      } catch (error) {
        this.handleError(error)
      }
    },
    async fetchGatewayProviders() {
      try {
        const response = await this.getGatewayProviders({ permission: eChannelsPermission.VIEW_TRANSFER_REPORT })
        const { status, data } = await response
        if (status === 200 && data) {
          this.gatewayProviderOptions = data
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async fetchOutwardTransfer() {
      try {
        this.isBusy = true
        const response = await this.getOutwardTransfer({
          search: this.getSearchFilterPayload(),
          page: this.currentPage,
          limit: this.perPage,
          permission: eChannelsPermission.VIEW_TRANSFER_REPORT,
        })
        const { data } = response
        if (data.data !== null) {
          this.isBusy = false
          this.outwardTransferList = data.data.map(item => ({
            ...item,
            amount: toBalance(item.amount),
            fee: toBalance(item.fee),
          }))
          this.totalCount = data.recordCount
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async exportOutwardTransfers(fileExportFormat) {
      try {
        this.showSpinner = true
        const response = await this.downloadOutwardTransfers({
          search: this.getSearchFilterPayload(),
          permission: eChannelsPermission.VIEW_TRANSFER_REPORT,
          fileExportFormat,
        })
        if (response.status === 200) {
          // Determine file type
          let fileType
          let fileExtension

          if (fileExportFormat === 2) {
            fileType = 'text/csv'
            fileExtension = 'csv'
          }

          if (fileExportFormat === 1) {
            fileType = 'application/vnd.ms-excel'
            fileExtension = 'xlsx'
          }
          const fileLabel = fileExtension === 'csv' ? 'CSV' : 'Excel'

          const blob = new Blob([response.data], { type: fileType })
          FileSaver.saveAs(blob, `Outward_Transfer_Reports_${new Date().toISOString().slice(0, 10)}.${fileExtension}`)
          this.alertSuccess({ text: this.$t(`Your request to download Outward Transfer Report as ${fileLabel} was successful`) })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },
    async csvExport() {
      await this.exportOutwardTransfers(2)
    },
    async excelExport() {
      await this.exportOutwardTransfers(1)
    },

    async onSelectRequeryStatus(items) {
      try {
        this.isBusy = true
        const response = await this.performRequeryStatus({
          transactionReference: items.transactionReference,
        })
        const { status } = response
        if (status === 200) {
          this.alertSuccess({ text: 'Your request to Requery Status was successful' })
          this.fetchOutwardTransfer()
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async onSelectRetryTransfer(items) {
      try {
        this.isBusy = true
        const response = await this.performRetryTransfer({ recordId: items.id })
        const { status } = response
        if (status === 200) {
          this.alertSuccess({ text: 'Your request to Retry Transfer was successful' })
          this.fetchOutwardTransfer()
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async onPrintReceipt(data) {
      this.printableData = data

      // Delay window.print() for 2 sec until data fetched
      setTimeout(() => {
        window.print()
      }, 200)
    },
    async onDownloadReceipt(transactionID) {
      try {
        this.isBusy = true
        const responseType = 'blob'
        const response = await this.generateTransactionReceipt({ transactionID, responseType })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'application/pdf' })
          FileSaver.saveAs(blob, 'Transaction Receipt.pdf')
          this.alertSuccess({ text: 'Your request to download Transaction Receipt was successful' })
        }
      } catch (error) {
        const errorString = JSON.parse(await error.response.data.text())
        this.alertError({ text: `Your request to download Transaction Receipt failed. Reason: ${errorString.errors[0].errorMessage}` })
      } finally {
        this.isBusy = false
      }
    },
    async fetchUsers() {
      try {
        this.isLoadingInitiatorName = true
        const formData = {
          status: 'Active',
        }
        const response = await this.getUsers({
          formData,
          permission: 'VIEW_TRANSFER_REPORT',
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.usersOptions = data.data
            // eslint-disable-next-line no-nested-ternary
            .map(item => ({ label: `${item?.firstname ?? ''} ${item?.lastname ?? ''} ${item?.othernames ?? ''}`, value: item.id })).sort((a, b) => (a.label === b.label ? 0 : a.label < b.label ? -1 : 1))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isLoadingInitiatorName = false
      }
    },
    async onSelectOutwardTransfer(items) {
      this.item = items
    },
    resetFilters() {
      this.filter = getDefaultFilter()
    },
    resetFilter() {
      this.filter = getDefaultFilter()
      this.fetchOutwardTransfer(
        this.currentPage = 1,
        this.perPage = 10,
        this.totalCount = 1,
      )
    },

  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

/* Hide print content on screen */
@media screen {
  #prints {
    display: none;
  }
  .print-footer {
    display: none;
  }
}

/* Screen styles */
img {
  max-width: 100%;
  height: auto;
}
.brand-header {
  text-align: center;
  margin-bottom: 2rem;
  padding-top: 1rem;
}

.amount-section {
  text-align: center;
  margin-bottom: 2rem;
  padding: 1.5rem;
  margin: 0 1rem 2rem 1rem;
}

.amount-label {
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
  color: #666666;
}

.amount-value {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 0.7rem;
  color: #333333;
}

.status-button {
  background: linear-gradient(45deg, #28a745, #20c997);
  border: none;
  border-radius: 25px;
  padding: 0.75rem 2rem;
  font-weight: 600;
  text-transform: capitalize;
  letter-spacing: 1px;
  color: white;
  box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
}

.transaction-details {
  background: transparent;
  border: none;
  position: relative;
}

.transaction-details .inner {
  content: '';
  max-width: 200px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(0deg);
  font-size: 4rem;
  font-weight: 800;
  color: rgba(0, 100, 255, 0.08);
  letter-spacing: 8px;
  z-index: 2;
  opacity: 0.4;
  pointer-events: none;
  white-space: nowrap;
}

.receipt-logo {
  max-width: 60px;
  position: relative;
  top: 0;
  left: 0;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.detail-label {
  font-size: 0.9rem;
  color: var(--bs-secondary-color);
  font-weight: 500;
}

.detail-value {
  font-weight: 600;
  color: var(--bs-body-color);
  text-align: right;
  max-width: 60%;
  word-break: break-word;
}

.receipt-title {
  font-weight: 700;
  color: var(--bs-secondary-color);
  text-align: right;
  max-width: 60%;
  word-break: break-word;
}

.disclaimer {
  margin-top: 2rem;
  text-align: center;
  color: #555555;
  font-size: 0.7rem;
  line-height: 1.5;
  padding: 1.5rem;
  margin: 2rem 1rem 1rem 1rem;
}

/* Responsive adjustments */
@media (max-width: 576px) {
  .transaction-container {
    padding: 10px;
  }

  .amount-value {
    font-size: 2.5rem;
  }

  .brand-title {
    font-size: 2rem;
  }

  .detail-value {
    font-size: 0.9rem;
    max-width: 55%;
  }

  .receipt-title {
    font-size: 1.5rem;
    max-width: 55%;
  }
}

/* Animation for status button */
.status-button {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
  }
  50% {
    box-shadow: 0 4px 20px rgba(40, 167, 69, 0.5);
  }
  100% {
    box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
  }
}

/* Print styles - Fixed to prevent blank page and extra pages */
@media print {
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  html, body {
    height: auto;
    margin: 0;
    padding: 0;
    overflow: visible;
    page-break-after: avoid;
    page-break-before: avoid;
  }

  body * {
    visibility: hidden;
  }

  #transaction-printer,
  #transaction-printer * {
    visibility: visible;
  }

  #prints,
  #prints * {
    visibility: visible;
  }

  #prints {
    position: absolute;
    left: 50%;
    top: 0;
    transform: translateX(-50%);
    margin: 0;
    padding: 0;
    width: 150mm;
    max-width: 100%;
  }

  .transaction-container {
    background-color: white !important;
    min-height: auto !important;
    padding: 10mm !important;
    margin: 0 !important;
  }

  .card,
  .card-body,
  .receipt-card {
    box-shadow: none !important;
    border: none !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .amount-section {
    page-break-inside: avoid;
    margin: 5mm 0 !important;
    padding: 5mm !important;
  }

  .transaction-details {
    page-break-inside: avoid;
  }

  .detail-item {
    page-break-inside: avoid;
  }

  .detail-item {
    page-break-inside: avoid;
    border-bottom: none !important;
    border-top: none !important;
    border: none !important;
  }

  .list-group-item {
    border: none !important;
  }

  .detail-item::after {
    display: none !important;
  }

  .disclaimer {
    color: #555555 !important;
    page-break-inside: avoid;
    page-break-before: avoid;
    margin: 5mm 0 0 0 !important;
    padding: 5mm !important;
  }

  .status-button {
    background: #28a745 !important;
    animation: none !important;
  }

  .inner {
    opacity: 0.1 !important;
  }

  /* Remove any trailing space */
  p {
    margin: 0 !important;
    padding: 0 !important;
  }

  .b-card,
  .overflow-hidden {
    overflow: visible !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  .print-footer {
    text-align: center;
    font-size: 14px;
    color: #555;
    border-top: 1px solid #ccc;
    padding: 6px 0;
    background: white;
    margin-top: 8px;
  }
}
</style>
